import type { IFrameTransitionAcceleratedStatus } from '@/helpers/playerDiagnostics/detectFrameTransitionAccelerated'
import * as Sentry from '@sentry/vue'

const MESSAGE = 'Player frame transition accelerated'

const MAX_TAG_LEN = 120

/** Cap Sentry event volume when a looping playlist keeps ending frames early. */
export const FRAME_TRANSITION_ACCELERATED_SENTRY_COOLDOWN_MS = 15 * 60 * 1000

function truncateTag(value: string): string {
  return value.length <= MAX_TAG_LEN ? value : `${value.slice(0, MAX_TAG_LEN - 1)}…`
}

let lastReportedFrameId: string | null = null
let lastReportedAtMs = 0

/**
 * Rising-edge Sentry issue when a frame ends well before its configured duration (#579).
 * Filter: message `Player frame transition accelerated`, tag `player_frame_transition_accelerated`.
 *
 * Fingerprint is stable (no frame UUID) so Sentry does not open one issue per playlist frame
 * (PLAYER-12…18). frameId stays in tags/extra. Cooldown limits event spam on looping playlists.
 */
export function reportFrameTransitionAcceleratedToSentry(status: IFrameTransitionAcceleratedStatus): void {
  if (!status.suspected) {
    lastReportedFrameId = null
    return
  }
  const frameId = status.frameId ?? ''
  if (frameId !== '' && lastReportedFrameId === frameId) {
    return
  }
  const nowMs = Date.now()
  if (lastReportedAtMs > 0 && nowMs - lastReportedAtMs < FRAME_TRANSITION_ACCELERATED_SENTRY_COOLDOWN_MS) {
    return
  }
  lastReportedFrameId = frameId !== '' ? frameId : null
  lastReportedAtMs = nowMs

  try {
    Sentry.addBreadcrumb({
      category: 'player_diagnostics_fault',
      message: 'player_diagnostics_fault_frame_transition_accelerated',
      level: 'warning',
      data: {
        code: 'frame_transition_accelerated',
        detailMs: status.earlyByMs,
        dwellMs: status.dwellMs,
        configuredDurationMs: status.configuredDurationMs,
        frameId: status.frameId ?? undefined
      }
    })

    Sentry.captureMessage(MESSAGE, {
      level: 'warning',
      tags: {
        player_frame_transition_accelerated: 'true',
        player_diagnostics_fault: 'frame_transition_accelerated',
        ...(frameId !== '' ? { player_frame_id: truncateTag(frameId) } : {})
      },
      fingerprint: ['player-frame-transition-accelerated'],
      extra: {
        earlyByMs: status.earlyByMs,
        dwellMs: status.dwellMs,
        configuredDurationMs: status.configuredDurationMs,
        frameId: status.frameId
      }
    })
  }
  catch {
    /* Sentry not initialized or unavailable */
  }
}

/** Test-only reset of dedupe state. */
export function resetFrameTransitionAcceleratedSentryDedupeForTests(): void {
  lastReportedFrameId = null
  lastReportedAtMs = 0
}
